import React, { useEffect, useState } from 'react';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { getUsers, deleteUser, setCurrentUser } from '$/store/user/actions';
import { connect } from 'react-redux';
import DeleteButton from '$/components/grid/DeleteButton';
import { Link } from 'react-router-dom';
import HeadingBlock from '$/components/global/heading';
import Modal from '$/components/global/modal';
import AddUser from './add-user';
import BixButton from '$/components/global/bixButton';
import './index.scss';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import dayjs from 'dayjs';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi } from 'ag-grid-enterprise';
import BulkImportUsers from '$/pages/admin/user/bulk-import-users';

interface IUserProps {
  isLoading?: boolean;
  error?: Bixby.Models.BixbyError;
  users: Bixby.Models.UserSlim[];
  dispatchGetUsers: () => Promise<void>;
  dispatchDeleteUser: (user: Bixby.Models.UserSlim) => Promise<void>;
  dispatchSetUser: (user?: Bixby.Models.UserSlim) => Promise<void>;
}

const UserList: React.FC<IUserProps> = props => {
  const { users, isLoading, dispatchGetUsers, dispatchDeleteUser, dispatchSetUser } = props;
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isRemovingUser, setIsRemovingUser] = useState(false);
  const [currentUser, setCurrentUser] = useState({} as Bixby.Models.UserSlim);
  const [bulkImportModal, setBulkImportModal] = useState(false);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  useEffect(() => {
    const getUserDispatcher = async () => {
      await dispatchGetUsers();
      await dispatchSetUser(undefined);
    };
    getUserDispatcher();
  }, [dispatchGetUsers, dispatchSetUser]);

  const handleDeleteUser = async () => {
    await dispatchDeleteUser(currentUser);
    await dispatchGetUsers();
    setIsRemovingUser(false);
  };

  const handleAddModal = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setIsCreatingUser(true);
  };

  const handleBulkImportModal = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setBulkImportModal(true);
  };

  const handleDeleteModal = (row: Bixby.Models.UserSlim) => {
    setCurrentUser(row);
    setIsRemovingUser(true);
    return Promise.resolve();
  };

  const handleModalDismiss = () => {
    setIsCreatingUser(false);
    setIsRemovingUser(false);
  };

  const handleBulkImportModalDismiss = () => {
    setBulkImportModal(false);
  };

  const handleSubmit = () => {
    setIsCreatingUser(false);
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };
  const lastNameCellRenderer = (props: any) => (
    <Link className="link_underline" to={`/admin/user/${props.data.id}`}>
      {props.data.lastName}
    </Link>
  );
  const deleteButtonCellRenderer = (props: any) => <DeleteButton row={props.data} deleter={handleDeleteModal} />;
  const clientCellRenderer = (props: any) => (
    <Link className="link_underline" to={`/admin/client/${props.data.client_Id}`}>
      {props.data.client_Name}
    </Link>
  );
  const firstLoginDateCellRenderer = (props: any) => {
    const firstLoginDate: dayjs.Dayjs | null = props.data.firstLoginDate ? dayjs.utc(props.data.firstLoginDate) : null;
    const displayDate: string | null =
      firstLoginDate && firstLoginDate.isValid() ? Bixby.Utils.formatDate(firstLoginDate) : null;
    return displayDate ? <div title={displayDate}>{displayDate}</div> : <div>Access Pending</div>;
  };
  const lastLoginDateCellRenderer = (props: any) => {
    const lastLoginDate: dayjs.Dayjs | null = props.data.lastLoginDate ? dayjs.utc(props.data.lastLoginDate) : null;
    const displayDate: string | null =
      lastLoginDate && lastLoginDate.isValid() ? Bixby.Utils.formatDateTime(lastLoginDate) : null;
    return displayDate ? <div title={displayDate}>{displayDate}</div> : <div>Access Pending</div>;
  };
  const isActiveCellRenderer = (props: any) => (props.data.isActive ? <div>Yes</div> : <div>No</div>);

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <div className="user_list">
        <HeadingBlock
          title={'Manage Users'}
          addButton={{ enabled: true, text: 'Add New User', onClick: handleAddModal }}
          importButton={{ enabled: true, text: 'Bulk Import User', onClick: handleBulkImportModal }}
        />
        {users && !Bixby.Utils.isNullOrEmpty(users) ? (
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              <AgGridReact
                defaultColDef={{
                  flex: 1,
                  minWidth: 100,
                  sortable: true,
                  resizable: true,
                  lockPinned: true,
                  filter: 'agNumberColumnFilter',
                  cellClass: 'align-center',
                }}
                onGridReady={onGridReady}
                rowData={users}
                suppressDragLeaveHidesColumns={true}
                suppressMovableColumns={true}
                animateRows={true}
                rowHeight={35}
                headerHeight={35}
                components={{
                  lastNameCellRenderer,
                  deleteButtonCellRenderer,
                  clientCellRenderer,
                  firstLoginDateCellRenderer,
                  lastLoginDateCellRenderer,
                  isActiveCellRenderer,
                }}
                columnDefs={[
                  {
                    headerName: '',
                    cellClass: 'align-left',
                    cellRenderer: 'deleteButtonCellRenderer',
                    maxWidth: 65,
                  },
                  {
                    field: 'lastName',
                    headerName: 'Last Name',
                    width: 200,
                    filter: 'agTextColumnFilter',
                    cellClass: 'align-left',
                    cellRenderer: 'lastNameCellRenderer',
                  },
                  {
                    field: 'firstName',
                    headerName: 'First Name',
                    width: 200,
                    filter: 'agTextColumnFilter',
                    cellClass: 'align-left',
                    cellRenderer: 'firstNameCellRenderer',
                  },
                  {
                    field: 'client_Name',
                    headerName: 'Client Name',
                    width: 200,
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    floatingFilterComponentParams: { suppressFilterButton: true },
                    suppressMenu: true,
                    cellClass: 'align-left',
                    cellRenderer: 'clientCellRenderer',
                  },
                  { field: 'email', headerName: 'User ID', filter: 'agTextColumnFilter' },
                  { field: 'title_Name', headerName: 'Title', filter: 'agTextColumnFilter' },
                  { field: 'industry_Name', headerName: 'Industry', filter: 'agTextColumnFilter' },
                  {
                    field: 'isActive',
                    headerName: 'Is Active',
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'isActiveCellRenderer',
                  },
                  {
                    field: 'firstLoginDate',
                    headerName: 'Date of 1st Access',
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'firstLoginDateCellRenderer',
                  },
                  {
                    field: 'lastLoginDate',
                    headerName: 'Last Login',
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'lastLoginDateCellRenderer',
                  },
                ]}></AgGridReact>
            </div>
          </div>
        ) : (
          <NoDataFound dataType="users" />
        )}
        <AddUser active={isCreatingUser} onAddSubmit={handleSubmit} onCloseModal={handleModalDismiss} />
        <Modal title="" active={isRemovingUser} forForm onClose={handleModalDismiss}>
          <p>Are you sure you want to delete this user?</p>
          <div className="button_side_wrap">
            <BixButton onClick={handleDeleteUser}>Delete</BixButton>
            <BixButton light={true} onClick={handleModalDismiss}>
              Cancel
            </BixButton>
          </div>
        </Modal>
        <BulkImportUsers
          active={bulkImportModal}
          onImportSubmit={handleBulkImportModalDismiss} // close modal on submit
          onCloseModal={handleBulkImportModalDismiss}
        />
      </div>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.userState.isLoading,
    error: state.userState.error,
    users: state.userState.users,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetUsers: () => dispatch(getUsers()),
  dispatchDeleteUser: (user: Bixby.Models.UserSlim) => dispatch(deleteUser(user)),
  dispatchSetUser: (user?: Bixby.Models.UserSlim) => dispatch(setCurrentUser(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserList);
